import { Table } from 'antd'
import { useParams } from 'react-router-dom'
import { useStatsSingleProject } from '../../../hooks/useStatsSingleProject'
import { dataStore } from '../../../store/dataStore'
import { useEffect, useRef } from 'react'

// css for table - clear distinction between site headers
export const siteColumnStyle = {
  borderLeft: '1px solid #000', // Thicker left border
} as const

export interface TableDataType {
  weekStartDate: string
  weekNumber: number
  [key: string]: any
}

/** Data kun fra Sitetool. Lavet af Henrik */
export default function CompletedVisits() {
  const { projectId } = useParams() as { projectId: string }
  const { tableData, sites } = useStatsSingleProject(projectId)

  const tableRef = useRef(null)

  useEffect(() => {
    if (tableRef.current) {
      // @ts-ignore
      const tableBody = tableRef.current.querySelector('.ant-table-body')
      if (tableBody) {
        tableBody.scrollTop = tableBody.scrollHeight
      }
    }
  }, [tableData])

  console.log('Tabledata:', tableData)

  const transformedData: TableDataType[] = tableData.reduce((acc, item) => {
    const siteIdArray = item.siteId.split(';')
    siteIdArray.forEach((siteId) => {
      // week start monday
      const firstEventDate = item.weekStartDate // Assuming this is the original date
      const firstEventDateDayOfWeek = new Date(firstEventDate).getDay()
      const diff = (1 - firstEventDateDayOfWeek) * 1000 * 60 * 60 * 24
      const adjustedWeekStartDate = new Date(new Date(firstEventDate).getTime() + diff)

      let existingEntry = acc.find(
        (entry) =>
          entry.weekStartDate === formatDateToDDMMMYYYY(adjustedWeekStartDate) && entry.weekNumber === item.weekNumber
      )
      if (!existingEntry) {
        existingEntry = {
          weekStartDate: formatDateToDDMMMYYYY(adjustedWeekStartDate), // Use adjusted date here
          weekNumber: item.weekNumber,
        }
        acc.push(existingEntry)
      }
      existingEntry[`screenings-${siteId}`] = (existingEntry[`screenings-${siteId}`] || 0) + item.screenings
      existingEntry[`randomisations-${siteId}`] = (existingEntry[`randomisations-${siteId}`] || 0) + item.randomisations
    })
    return acc
  }, [] as TableDataType[])

  // Debugging: Log the transformed data to check adjusted week start dates
  console.log('Table data after transform:', transformedData)

  const selectedSiteCustomIds = dataStore((state) => state.statsSelectedSites)
  const selectedSiteIds = sites
    .filter((s) => s.projectId === projectId && s.customId && selectedSiteCustomIds?.includes(s.customId))
    .map((s) => s._id)

  // Explicitly type siteIds as string[]
  const siteIds: string[] = sites.map((site) => site._id)

  const columns = defineColumns(siteIds, sites, transformedData)
  const newColumns = columns.filter((column) => {
    // Check if the column key is in the checkedList
    if (column.key === 'weekStartDate' || column.key === 'weekNumber') {
      return true // Always include these columns
    }
    return selectedSiteIds?.includes(column.key as string)
  })

  return (
    <div ref={tableRef}>
      <Table<TableDataType>
        columns={newColumns}
        dataSource={transformedData}
        rowKey="weekStartDate"
        bordered
        size="small"
        pagination={{ pageSize: 2000, hideOnSinglePage: true }}
        scroll={{ y: 450 }}
      />
    </div>
  )
}

// date formatter so it looks nice
export function formatDateToDDMMMYYYY(isoString) {
  const date = new Date(isoString)
  const day = date.getDate()
  const month = date.toLocaleString('default', { month: 'short' })
  const year = date.getFullYear()
  return `${day}-${month}-${year}`
}

// Define columns outside the component
function defineColumns(siteIds: string[], sites, transformedData: TableDataType[]) {
  const total: { [compositeId: string]: number } = {}

  for (const row of transformedData) {
    for (const compositeId of Object.keys(row)) {
      total[compositeId] = (total[compositeId] ?? 0) + row[compositeId]
    }
  }

  return [
    {
      title: 'Week start',
      dataIndex: 'weekStartDate',
      key: 'weekStartDate',
      width: 120,
      sorter: (a: TableDataType, b: TableDataType) => {
        const dateA = new Date(a.weekStartDate)
        const dateB = new Date(b.weekStartDate)
        return dateA.getTime() - dateB.getTime()
      },
      render: (text, record, index) => {
        const isLastRow = index === transformedData.length - 1
        const style = isLastRow ? { fontWeight: 'bold', lineHeight: '10px' } : {}

        return (
          <p style={{ textAlign: 'right', ...style }}>
            {formatDateToDDMMMYYYY(text)}
            {isLastRow && (
              <span className="text-muted" style={{ fontSize: 10 }}>
                <br />
                Current week
              </span>
            )}
          </p>
        )
      },
    },
    {
      title: 'Week',
      dataIndex: 'weekNumber',
      key: 'weekNumber',
      width: 80,
      sorter: (a, b) => a.weekNumber - b.weekNumber,
      render: (text, record, index) => {
        const isLastRow = index === transformedData.length - 1
        const style = isLastRow ? { fontWeight: 'bold', lineHeight: '10px' } : {}
        return (
          <p style={{ ...style }}>
            {text}
            {isLastRow && (
              <span className="text-muted" style={{ fontSize: 10 }}>
                <br />
                &nbsp;
              </span>
            )}
          </p>
        )
      },
    },
    ...siteIds.map((siteId) => {
      const site = sites.find((s) => s._id === siteId)
      const siteName = site ? site.officialName : 'Unknown site'

      const renderZeroAsEmpty = (value) => {
        return value === 0 ? '' : value
      }

      return {
        title: `${siteName}`,
        key: siteId, // Ensure the key matches the siteId for filtering
        onHeaderCell: () => ({ style: { ...siteColumnStyle, fontWeight: 'bold', color: '#164369' } }),
        children: [
          {
            title: (
              <span>
                Screened
                <br />
                <small className="text-muted">Total: {total[`screenings-${siteId}`] ?? 0}</small>
              </span>
            ),
            dataIndex: `screenings-${siteId}`,
            key: `screenings-${siteId}`,
            onHeaderCell: () => ({ style: { ...siteColumnStyle, fontSize: 12 } }),
            onCell: () => ({ style: siteColumnStyle }),
            sorter: (a, b) => (a[`screenings-${siteId}`] || 0) - (b[`screenings-${siteId}`] || 0),
            render: renderZeroAsEmpty,
          },
          {
            title: (
              <span>
                Randomized
                <br />
                <small className="text-muted">Total: {total[`randomisations-${siteId}`] ?? 0}</small>
              </span>
            ),
            dataIndex: `randomisations-${siteId}`,
            onHeaderCell: () => ({ style: { fontSize: 12 } }),
            key: `randomisations-${siteId}`,
            sorter: (a, b) => (a[`randomisations-${siteId}`] || 0) - (b[`randomisations-${siteId}`] || 0),
            render: renderZeroAsEmpty,
          },
        ],
      }
    }),
  ]
}
